/**
 * IFRAP-AI.fr - Page Administration Alertes
 * Configuration des alertes email et préférences
 */

import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { Bell, Mail, Send, Check, AlertTriangle, Settings, RefreshCw } from 'lucide-react';

const AlertsPage = () => {
  const [status, setStatus] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    enabled: true,
    veille_think_tank: true,
    veille_media: false,
    kpi_alerts: true,
    daily_digest: true,
    weekly_digest: false,
    frequency: 'daily',
    keywords: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, prefsRes] = await Promise.all([
        api.get('/api/alerts/status'),
        api.get('/api/alerts/preferences'),
      ]);
      
      setStatus(statusRes.data);
      setPreferences(prefsRes.data);
      
      if (prefsRes.data.configured) {
        setFormData({
          email: prefsRes.data.email,
          enabled: prefsRes.data.enabled,
          veille_think_tank: prefsRes.data.veille_think_tank,
          veille_media: prefsRes.data.veille_media,
          kpi_alerts: prefsRes.data.kpi_alerts,
          daily_digest: prefsRes.data.daily_digest,
          weekly_digest: prefsRes.data.weekly_digest,
          frequency: prefsRes.data.frequency,
          keywords: (prefsRes.data.keywords || []).join(', '),
        });
        setTestEmail(prefsRes.data.email);
      } else if (prefsRes.data.email) {
        setFormData(prev => ({ ...prev, email: prefsRes.data.email }));
        setTestEmail(prefsRes.data.email);
      }
    } catch (err) {
      console.error('Error fetching alert data:', err);
      setMessage({ type: 'error', text: 'Erreur lors du chargement' });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      await api.post('/api/alerts/preferences', {
        ...formData,
        keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k),
        think_tank_sources: ['all'],
      });
      
      setMessage({ type: 'success', text: 'Préférences enregistrées' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) return;
    
    setSaving(true);
    setMessage(null);

    try {
      await api.post('/api/alerts/test', { email: testEmail });
      setMessage({ type: 'success', text: `Email de test envoyé à ${testEmail}` });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de l\'envoi';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  const handleSendVeille = async (category) => {
    setSaving(true);
    setMessage(null);

    try {
      const res = await api.post('/api/alerts/send-veille', {
        email: formData.email,
        category: category,
      });
      setMessage({ type: 'success', text: `Alerte ${category} envoyée (${res.data.articles_count} articles)` });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de l\'envoi';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  const handleSendDigest = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const res = await api.post('/api/alerts/send-digest', { email: formData.email });
      setMessage({ type: 'success', text: `Digest envoyé (${res.data.articles_count} articles)` });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de l\'envoi';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[#00FF88] animate-spin" />
      </div>
    );
  }

  const isEmailConfigured = status?.email_service?.configured;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono text-[#00FF88]">Alertes Email</h1>
          <p className="text-gray-400 text-sm mt-1">Configuration des notifications</p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
          isEmailConfigured 
            ? 'bg-green-500/20 text-green-400' 
            : 'bg-red-500/20 text-red-400'
        }`}>
          {isEmailConfigured ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {isEmailConfigured ? 'SMTP configuré' : 'SMTP non configuré'}
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-500/10 border-green-500/30 text-green-400'
            : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          {message.text}
        </div>
      )}

      {/* SMTP Status */}
      {!isEmailConfigured && (
        <div className="bg-[#1a1a2e] border border-yellow-500/30 rounded-lg p-4">
          <h3 className="text-yellow-400 font-medium flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Configuration SMTP requise
          </h3>
          <p className="text-gray-400 text-sm mt-2">
            Pour activer les alertes email, configurez les variables d'environnement suivantes dans le fichier <code className="bg-black/30 px-1 rounded">.env</code> :
          </p>
          <pre className="bg-black/30 p-3 rounded mt-3 text-sm text-gray-300 overflow-x-auto">
{`SMTP_HOST=smtp.votre-serveur.com
SMTP_PORT=587
SMTP_USER=votre-email@domaine.com
SMTP_PASSWORD=votre-mot-de-passe
SMTP_FROM=alertes@ifrap-ai.fr
SMTP_FROM_NAME=IFRAP-AI Alertes`}
          </pre>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Preferences Form */}
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6">
          <h2 className="text-lg font-mono text-[#00FF88] mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Préférences
          </h2>

          <form onSubmit={handleSavePreferences} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Email de réception</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-black/30 border border-gray-700 rounded px-3 py-2 text-white focus:border-[#00FF88] focus:outline-none"
                placeholder="votre@email.com"
                required
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="w-4 h-4 accent-[#00FF88]"
              />
              <label htmlFor="enabled" className="text-gray-300">Alertes activées</label>
            </div>

            <div className="border-t border-gray-800 pt-4">
              <p className="text-sm text-gray-400 mb-3">Types d'alertes</p>
              
              <div className="space-y-2">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.veille_think_tank}
                    onChange={(e) => setFormData({ ...formData, veille_think_tank: e.target.checked })}
                    className="w-4 h-4 accent-[#00FF88]"
                  />
                  <span className="text-gray-300">Veille Think Tanks</span>
                </label>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.veille_media}
                    onChange={(e) => setFormData({ ...formData, veille_media: e.target.checked })}
                    className="w-4 h-4 accent-[#00FF88]"
                  />
                  <span className="text-gray-300">Veille Médias</span>
                </label>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.kpi_alerts}
                    onChange={(e) => setFormData({ ...formData, kpi_alerts: e.target.checked })}
                    className="w-4 h-4 accent-[#00FF88]"
                  />
                  <span className="text-gray-300">Alertes KPI (seuils)</span>
                </label>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.daily_digest}
                    onChange={(e) => setFormData({ ...formData, daily_digest: e.target.checked })}
                    className="w-4 h-4 accent-[#00FF88]"
                  />
                  <span className="text-gray-300">Digest quotidien</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Mots-clés (optionnel)</label>
              <input
                type="text"
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                className="w-full bg-black/30 border border-gray-700 rounded px-3 py-2 text-white focus:border-[#00FF88] focus:outline-none"
                placeholder="dette, réforme, fiscalité"
              />
              <p className="text-xs text-gray-500 mt-1">Séparez par des virgules</p>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full bg-[#00FF88] text-black font-medium py-2 rounded hover:bg-[#00FF88]/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer les préférences'}
            </button>
          </form>
        </div>

        {/* Test & Actions */}
        <div className="space-y-6">
          {/* Test Email */}
          <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-mono text-[#00FF88] mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Test Email
            </h2>

            <div className="flex gap-2">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="flex-1 bg-black/30 border border-gray-700 rounded px-3 py-2 text-white focus:border-[#00FF88] focus:outline-none"
                placeholder="test@email.com"
              />
              <button
                onClick={handleSendTest}
                disabled={saving || !isEmailConfigured || !testEmail}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Tester
              </button>
            </div>
          </div>

          {/* Manual Actions */}
          <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-mono text-[#00FF88] mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Envoi manuel
            </h2>

            <div className="space-y-3">
              <button
                onClick={() => handleSendVeille('Think Tanks')}
                disabled={saving || !isEmailConfigured || !formData.email}
                className="w-full bg-purple-600/20 border border-purple-500/30 text-purple-400 px-4 py-3 rounded hover:bg-purple-600/30 transition-colors disabled:opacity-50 text-left"
              >
                <div className="font-medium">Alerte Veille Think Tanks</div>
                <div className="text-sm opacity-70">Dernières publications des think tanks</div>
              </button>

              <button
                onClick={() => handleSendVeille('Médias')}
                disabled={saving || !isEmailConfigured || !formData.email}
                className="w-full bg-green-600/20 border border-green-500/30 text-green-400 px-4 py-3 rounded hover:bg-green-600/30 transition-colors disabled:opacity-50 text-left"
              >
                <div className="font-medium">Alerte Veille Médias</div>
                <div className="text-sm opacity-70">Dernières actualités économiques</div>
              </button>

              <button
                onClick={handleSendDigest}
                disabled={saving || !isEmailConfigured || !formData.email}
                className="w-full bg-orange-600/20 border border-orange-500/30 text-orange-400 px-4 py-3 rounded hover:bg-orange-600/30 transition-colors disabled:opacity-50 text-left"
              >
                <div className="font-medium">Digest Quotidien</div>
                <div className="text-sm opacity-70">Résumé complet de la journée</div>
              </button>
            </div>
          </div>

          {/* Status Info */}
          {status && (
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6">
              <h2 className="text-lg font-mono text-gray-400 mb-4">Statut système</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Serveur SMTP</span>
                  <span className="text-gray-300">{status.email_service?.smtp_host || 'Non configuré'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Port</span>
                  <span className="text-gray-300">{status.email_service?.smtp_port || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Expéditeur</span>
                  <span className="text-gray-300">{status.email_service?.from_email || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Utilisateurs configurés</span>
                  <span className="text-gray-300">{status.configured_users || 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertsPage;
